import { JobPosition as PflegiaJobPosition } from '@pflegia/types/api/job-position/job-position';
import { ScrollableSectionEnum } from '@pflegia/types/common/enums/scrollable-section-enum';

import { getPflegiaSections, pflegiaSectionOrder } from './get-pflegia-sections';

export const AB_TEST_SECTION_KEYS = {
  otherJobsCarousel: 'other-jobs-carousel',
  controlAnchor: 'control-anchor',
} as const;

export type JdpSectionKey = ScrollableSectionEnum | (typeof AB_TEST_SECTION_KEYS)[keyof typeof AB_TEST_SECTION_KEYS];

export interface JdpSectionsForVariant {
  orderedSections: JdpSectionKey[];
  sections: ScrollableSectionEnum[];
  visibleSections: Record<ScrollableSectionEnum, boolean>;
}

const TEST_ORDER: JdpSectionKey[] = [
  ScrollableSectionEnum.Salary,
  ScrollableSectionEnum.Location,
  ScrollableSectionEnum.Career,
  ScrollableSectionEnum.ShiftSystem,
  ScrollableSectionEnum.Responsibilities,
  ScrollableSectionEnum.Profile,
  ScrollableSectionEnum.Summary,
  ScrollableSectionEnum.ContactPerson,
  AB_TEST_SECTION_KEYS.otherJobsCarousel,
  ScrollableSectionEnum.AboutUs,
  ScrollableSectionEnum.ClientAssignment,
  ScrollableSectionEnum.Differentiations,
  ScrollableSectionEnum.OpeningHours,
  ScrollableSectionEnum.Benefits,
  ScrollableSectionEnum.Onboarding,
  ScrollableSectionEnum.Gallery,
  ScrollableSectionEnum.EmployeeTestimony,
  ScrollableSectionEnum.DailyWork,
  ScrollableSectionEnum.Insights,
];

const isScrollableSection = (key: JdpSectionKey): key is ScrollableSectionEnum =>
  Object.values(ScrollableSectionEnum).includes(key as ScrollableSectionEnum);

// Sections that render in the page body but must not appear as sticky-nav items.
const toNavSections = (sections: ScrollableSectionEnum[]): ScrollableSectionEnum[] =>
  sections.filter(section => section !== ScrollableSectionEnum.ContactPerson);

const filterVisibleSections = (order: JdpSectionKey[], visibleSections: Record<ScrollableSectionEnum, boolean>): JdpSectionKey[] =>
  order.filter(key => {
    if (isScrollableSection(key)) return visibleSections[key];
    return true;
  });

export const getJdpSectionsForVariant = (jobPosition: PflegiaJobPosition, isTest: boolean | null): JdpSectionsForVariant => {
  const base = getPflegiaSections(jobPosition);

  if (isTest === null) {
    return {
      orderedSections: filterVisibleSections(pflegiaSectionOrder, base.visibleSections),
      sections: toNavSections(base.sections),
      visibleSections: base.visibleSections,
    };
  }

  if (isTest) {
    const orderedSections = filterVisibleSections(TEST_ORDER, base.visibleSections);
    const navSections = toNavSections(orderedSections.filter(isScrollableSection));
    return { orderedSections, sections: navSections, visibleSections: base.visibleSections };
  }

  // control
  const orderedSections = filterVisibleSections([AB_TEST_SECTION_KEYS.controlAnchor, ...pflegiaSectionOrder], base.visibleSections);
  return { orderedSections, sections: toNavSections(base.sections), visibleSections: base.visibleSections };
};
